import {
  escape,
  stepToNode,
  offsetToPoint,
  DOMPosition,
  isNodePosition,
  DOMNodePosition,
  DOMEmptyPosition,
  isEmptyPosition,
  getOwnerDocument,
  isTextPosition,
  IGNORE_ATTRIBUTE,
} from './common';
import { isElement } from '../utils/dom';
import { html as HTML_NS } from '../xmlns';
import { someIterator } from '../utils';

export const enum OffsetType {
  CHARACTER,
  TEMPORAL,
  SPATIAL,
  TEMPORAL_SPATIAL,
}

export interface Parameter {
  readonly key: string;
  readonly values: ReadonlyArray<string>;
}

export class TextLocationAssertion {
  public constructor(public readonly before?: string, public readonly after?: string) { }

  public toString() {
    const { before, after } = this;
    return `${
      before !== undefined ? escape(before) : ''
    }${
      after !== undefined ? `,${escape(after)}` : ''
    }`;
  }

  public valueOf() {
    return this.toString();
  }
}

export class Assertion {
  public constructor(public readonly location?: TextLocationAssertion,
                     public readonly parameters: ReadonlyArray<Parameter> = []) { }

  public toString() {
    const { location, parameters } = this;
    return `${
      location !== undefined ? location.toString() : ''
    }${
      parameters.map((parameter) => {
        return `;${escape(parameter.key)}=${
          parameter.values.map(value => escape(value)).join(',')
        }`;
      }).join('')
    }`;
  }

  public valueOf() {
    return this.toString();
  }
}

export interface CharacterOffset {
  readonly type: OffsetType.CHARACTER;
  readonly offset: number;
}

export interface TemporalOffset {
  readonly type: OffsetType.TEMPORAL;
  readonly offset: number;
}

export interface SpatialOffset {
  readonly type: OffsetType.SPATIAL;
  readonly offsetX: number;
  readonly offsetY: number;
}

export interface TemporalSpatialOffset {
  readonly type: OffsetType.TEMPORAL_SPATIAL;
  readonly offset: number;
  readonly offsetX: number;
  readonly offsetY: number;
}

export type SomeOffset = CharacterOffset | TemporalOffset | SpatialOffset | TemporalSpatialOffset;

export class Offset {
  public readonly type: OffsetType;
  public readonly offset?: number;
  public readonly offsetX?: number;
  public readonly offsetY?: number;

  public constructor(offset: SomeOffset) {
    switch (offset.type) {
      case OffsetType.CHARACTER:
      case OffsetType.TEMPORAL:
        this.offset = offset.offset;
        break;
      case OffsetType.TEMPORAL_SPATIAL:
        this.offset = offset.offset;
      case OffsetType.SPATIAL:
        this.offsetX = offset.offsetX;
        this.offsetY = offset.offsetY;
    }
    this.type = offset.type;
  }

  public isCharacter(): this is CharacterOffset {
    return this.type === OffsetType.CHARACTER;
  }

  public isTemporal(): this is TemporalOffset {
    return this.type === OffsetType.TEMPORAL;
  }

  public isSpatial(): this is SpatialOffset {
    return this.type === OffsetType.SPATIAL;
  }

  public isTemporalSpatial(): this is TemporalSpatialOffset {
    return this.type === OffsetType.TEMPORAL_SPATIAL;
  }

  public hasTemporalValue(): this is TemporalOffset | TemporalSpatialOffset {
    return this.isTemporal() || this.isTemporalSpatial();
  }

  public hasSpatialValue(): this is SpatialOffset | TemporalSpatialOffset {
    return this.isSpatial() || this.isTemporalSpatial();
  }

  public toString() {
    if (this.isCharacter()) {
      return `:${this.offset}`;
    }
    if (this.isTemporal()) {
      return `~${this.offset}`;
    }
    if (this.isSpatial()) {
      return `@${this.offsetX}:${this.offsetY}`;
    }
    return `~${this.offset}@${this.offsetX}:${this.offsetY}`;
  }

  public valueOf() {
    return this.toString();
  }

  public compare(other?: Offset) {
    if (other === undefined) {
      if (!this.isCharacter()) {
        return 1;
      }
      return this.offset > 0 ? 1 : 0;
    }
    if (this.hasTemporalValue()) {
      if (other.hasTemporalValue()) {
        if (this.offset > other.offset) {
          return 1;
        }
        if (this.offset < other.offset) {
          return -1;
        }
      } else {
        return 1;
      }
    }
    if (this.hasSpatialValue()) {
      if (other.hasSpatialValue()) {
        if (this.offsetY > other.offsetY) {
          return 1;
        }
        if (this.offsetY < other.offsetY) {
          return -1;
        }
        if (this.offsetX > other.offsetX) {
          return 1;
        }
        if (this.offsetX < other.offsetX) {
          return -1;
        }
        return 0;
      }
      return 1;
    }
    if (other.hasSpatialValue()) {
      return -1;
    }
    if (this.isCharacter()) {
      if (!other.isCharacter()) {
        return -1;
      }
      if (this.offset > other.offset) {
        return 1;
      }
      if (this.offset < other.offset) {
        return -1;
      }
      return 0;
    }
    return 0;
  }
}

export class Child {
  public constructor(public readonly value: number = 0, public readonly id?: string) { }

  public toString() {
    const { value, id } = this;
    return `/${value}${id !== undefined ? `[${escape(id)}]` : ''}`;
  }

  public valueOf() {
    return this.toString();
  }

  public equals(other: Child) {
    return this.value === other.value && this.id === other.id;
  }

  public compare(other: Child) {
    if (this.value > other.value) {
      return 1;
    }
    if (this.value < other.value) {
      return -1;
    }
    return 0;
  }
}

export class LocalPath {
  public readonly children: ReadonlyArray<Child>;
  public constructor(...children: Child[]) {
    this.children = children;
  }

  public toString() {
    const { children } = this;
    return children.map(step => step.toString()).join('');
  }

  public valueOf() {
    return this.toString();
  }

  public eval(root: Element): DOMNodePosition | DOMEmptyPosition | undefined {
    const { children } = this;
    let node: Node = root;
    for (const step of children) {
      const { firstChild } = node;
      if (firstChild === null) {
        return;
      }
      const position = stepToNode(firstChild, step.value);
      if (position === undefined) {
        return;
      }
      if (isNodePosition(position)) {
        ({ node } = position);
        if (step.id !== undefined) {
          if (node !== null && isElement(node) && node.getAttribute('id') === step.id) {
            continue;
          }

          const ownerDocument = root.ownerDocument;
          if (ownerDocument === null) {
            continue;
          }

          const newNode = ownerDocument.getElementById(step.id);
          if (newNode !== null) {
            node = newNode;
            continue;
          }
          return;
        }
        continue;
      }
      if (step === children[children.length - 1]) {
        return position;
      }
      return;
    }
    return { node };
  }
}

export type LocalPaths = ReadonlyArray<LocalPath>;

class Redirect {
  private constructor() {}
  public static readonly instance = new Redirect();
}

type Step = Child | Offset | Redirect;

export class Path {
  public constructor(public readonly localPaths: LocalPaths = [],
                     public readonly offset?: Offset,
                     public readonly assertion?: Assertion) { }

  public toString() {
    const { localPaths, offset, assertion } = this;
    return `${
      localPaths.map(localPath => localPath.toString()).join('!')
    }${
      offset !== undefined ? offset.toString() : ''
    }${
      assertion !== undefined ? `[${assertion.toString()}]` : ''
    }`;
  }

  public valueOf() {
    return this.toString();
  }

  public toFragment() {
    return `epubcfi(${this.toString()})`;
  }

  public eval(root: Element): DOMPosition | undefined {
    let node : Node = root;
    const { localPaths, offset } = this;
    for (const localPath of localPaths) {
      const position = localPath.eval(node as Element);
      if (position === undefined) {
        return;
      }
      if (isEmptyPosition(position)) {
        if (localPath === localPaths[localPaths.length - 1]) {
          return position;
        }
        return;
      }
      ({ node } = position);
      if (localPath !== localPaths[localPaths.length - 1]) {
        if (!isElement(node)) {
          return;
        }
        if (node.namespaceURI !== HTML_NS) {
          return;
        }
        if (!((node): node is HTMLObjectElement => node.nodeName === 'object')(node)) {
          return;
        }
        let doc: Document | null;
        try { doc = node.contentDocument; } catch (e) { doc = null; }
        if (doc === null || doc.documentElement === null) {
          return;
        }
        node = doc.documentElement;
        continue;
      }
      if (!isElement(node) || node.hasAttribute(IGNORE_ATTRIBUTE)) {
        return offsetToPoint(node,
                             offset !== undefined && offset.isCharacter() ? offset.offset : 0);
      }
      if (offset !== undefined && offset.isCharacter()) {
        return;
      }
      return position;
    }
  }

  public prependParent(parent: LocalPath) {
    return new Path([parent, ...this.localPaths], this.offset, this.assertion);
  }

  public unshiftParent() {
    return new Path(this.localPaths.slice(1), this.offset, this.assertion);
  }

  public getFirstLocalPath() {
    return this.localPaths[0];
  }

  public compare(other: Path) {
    const otherIterator = other.iterate();
    let result : -1 | 0 | 1 = 0;
    if (someIterator(this.iterate(), (step) => {
      const { value, done } = otherIterator.next();
      if (done) {
        result = step instanceof Offset ? step.compare() : 1;
      } else if (step instanceof Redirect) {
        if (value instanceof Redirect) {
          result = 0;
          return false;
        }
        result = -1;
      } else if (value instanceof Redirect) {
        result = 1;
      } else if (step instanceof Offset && !step.isCharacter()) {
        if (value instanceof Offset && !value.isCharacter()) {
          result = step.compare(value);
          return result !== 0;
        }
        result = 1;
      } else if (value instanceof Offset && !value.isCharacter()) {
        result = -1;
      } else if (step instanceof Child) {
        if (value instanceof Child) {
          result = step.compare(value);
          return result !== 0;
        }
        result = 1;
      } else if (value instanceof Child) {
        result = -1;
      } else {
        result = step.compare(value);
      }
      return true;
    })) {
      return result;
    }
    const { value, done } = otherIterator.next();
    if (value instanceof Offset) {
      return -value.compare() as -1 | 0 | 1;
    }
    return done ? 0 : -1;
  }

  private *iterate(): Iterator<Step> {
    let index = 0;
    for (const localPath of this.localPaths) {
      for (const child of localPath.children) {
        yield child;
      }
      index += 1;
      if (index < this.localPaths.length) {
        yield Redirect.instance;
      }
    }
    if (this.offset !== undefined) {
      yield this.offset;
    }
  }
}

export class CFIRange {
  public constructor(public readonly parent: LocalPaths,
                     public readonly start: Path,
                     public readonly end: Path) { }

  public toString() {
    const { parent, start, end } = this;
    return `${parent.join('!')},${start},${end}`;
  }

  public valueOf() {
    return this.toString();
  }

  public toFragment() {
    return `epubcfi(${this})`;
  }

  public getStartPath() {
    const { parent, start } = this;
    return toFullPath(parent, start);
  }

  public getEndPath() {
    const { parent, end } = this;
    return toFullPath(parent, end);
  }

  public eval(root: Element) {
    const s = this.getStartPath().eval(root);
    if (s !== undefined) {
      const e = this.getEndPath().eval(root);
      if (e !== undefined) {
        const ownerDocument = getOwnerDocument(s);
        if (ownerDocument === null) {
          return;
        }

        const range = ownerDocument.createRange();
        if (isNodePosition(s)) {
          range.setStart(s.node, 0);
        } else if (isTextPosition(s)) {
          range.setStart(s.text, s.offset);
        } else if (s.next !== null) {
          range.setStartBefore(s.next);
        } else {
          range.setStartAfter(s.prev!);
        }
        if (isNodePosition(e)) {
          range.setEnd(e.node, 0);
        } else if (isTextPosition(e)) {
          range.setEnd(e.text, e.offset);
        } else if (e.next !== null) {
          range.setEndBefore(e.next);
        } else {
          range.setEndAfter(e.prev!);
        }
        return range;
      }
    }
  }

  public prependParent(parent: LocalPath) {
    return new CFIRange([parent, ...this.parent], this.start, this.end);
  }

  public unshiftParent() {
    return new CFIRange(this.parent.slice(1), this.start, this.end);
  }

  public getFirstLocalPath() {
    return this.parent[0];
  }

  public static create(start: Path, end: Path): CFIRange {
    if (start.compare(end) > 0) {
      return CFIRange.create(end, start);
    }
    const parent: LocalPath[] = [];
    const newStart: LocalPath[] = [];
    const newEnd: LocalPath[] = [];
    let index = 0;
    while (index < start.localPaths.length && index < end.localPaths.length) {
      const startLolcalPath = start.localPaths[index];
      const endLolcalPath = end.localPaths[index];
      const startSteps = startLolcalPath.children;
      const endSteps = endLolcalPath.children;
      let stepIndex = 0;
      while (stepIndex < startSteps.length && stepIndex < endSteps.length) {
        if (!startSteps[stepIndex].equals(endSteps[stepIndex])) {
          break;
        }
        stepIndex += 1;
      }
      if (stepIndex === startSteps.length) {
        parent.push(startLolcalPath);
        if (stepIndex === endSteps.length) {
          index += 1;
          continue;
        }
        newStart.push(new LocalPath());
        newEnd.push(new LocalPath(...endSteps.slice(stepIndex)));
      } else if (stepIndex === endSteps.length) {
        parent.push(endLolcalPath);
        newStart.push(new LocalPath(...startSteps.slice(stepIndex)));
        newEnd.push(new LocalPath());
      } else {
        parent.push(new LocalPath(...startSteps.slice(0, stepIndex)));
        newStart.push(new LocalPath(...startSteps.slice(stepIndex)));
        newEnd.push(new LocalPath(...endSteps.slice(stepIndex)));
      }
      index += 1;
      break;
    }
    if (index !== start.localPaths.length) {
      newStart.push(...start.localPaths.slice(index));
    }
    if (index !== end.localPaths.length) {
      newEnd.push(...end.localPaths.slice(index));
    }
    return new CFIRange(parent,
                        new Path(newStart, start.offset, start.assertion),
                        new Path(newEnd, end.offset, end.assertion));
  }
}

export type CFI = Path | CFIRange;

export function toFullPath(localPaths: LocalPaths, path: Path) {
  const local = localPaths.slice();
  if (local.length > 0 && path.localPaths.length > 0) {
    const last = local[local.length - 1];
    const children = last.children.concat(path.localPaths[0].children);
    local[local.length - 1] = new LocalPath(...children);
    local.push(...path.localPaths.slice(1));
  } else {
    local.push(...path.localPaths);
  }
  return new Path(local, path.offset);
}
